import { ArrowRight, Calendar, Activity, Users, TrendingUp, Clock, UserCheck } from "lucide-react";
import { Button } from "../ui/button";

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 pb-20 md:pt-36 md:pb-32 overflow-hidden noise-overlay"
    >
      {/* Background blobs */}
      <div className="hero-blob w-[500px] h-[500px] bg-emerald-300 top-[-100px] right-[-100px]" />
      <div className="hero-blob w-[400px] h-[400px] bg-emerald-200 bottom-[-50px] left-[-100px]" />
      <div className="hero-blob w-[250px] h-[250px] bg-amber-200 top-[40%] left-[30%]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left column - Text */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 mb-8 animate-fade-up opacity-0">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse-soft" />
              <span className="text-sm font-medium text-emerald-700">
                Plataforma para fisioterapeutas en Mexico
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight leading-[1.1] mb-6 animate-fade-up opacity-0 animate-stagger-1">
              El sistema operativo para la{" "}
              <span className="gradient-text">fisioterapia moderna</span>
            </h1>

            {/* Subheadline */}
            <p className="text-base md:text-lg text-slate-500 leading-relaxed max-w-lg mb-10 animate-fade-up opacity-0 animate-stagger-2">
              Gestione su clinica por{" "}
              <strong className="text-slate-700">Episodios de Atencion</strong>.
              Historia clinica, agenda, ejercicios y portal de paciente — todo en una sola plataforma.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-up opacity-0 animate-stagger-3">
              <Button
                data-testid="hero-cta-button"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-6 text-base rounded-full shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 transition-shadow duration-200 group"
              >
                Empezar prueba gratis
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
              <Button
                data-testid="hero-demo-button"
                variant="outline"
                className="border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/50 text-slate-700 font-medium px-8 py-6 text-base rounded-full transition-colors duration-200"
              >
                Ver demostracion
              </Button>
            </div>

            {/* Trust metrics */}
            <div className="flex items-center gap-8 animate-fade-up opacity-0 animate-stagger-4">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {["bg-emerald-400", "bg-teal-400", "bg-cyan-400", "bg-emerald-300"].map(
                    (bg, i) => (
                      <div
                        key={i}
                        className={`w-8 h-8 rounded-full ${bg} border-2 border-white flex items-center justify-center`}
                      >
                        <span className="text-white text-xs font-bold">
                          {["LR", "MC", "JP", "AS"][i]}
                        </span>
                      </div>
                    )
                  )}
                </div>
                <span className="text-sm text-slate-500">
                  <strong className="text-slate-700">50+</strong> clinicas activas
                </span>
              </div>
            </div>
          </div>

          {/* Right column - Dashboard preview */}
          <div className="relative animate-slide-in-right opacity-0 animate-stagger-2">
            {/* Dashboard mock */}
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-12px_rgba(0,0,0,0.15)] border border-slate-200/60 bg-white p-6">
              {/* Top bar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <Activity className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-heading font-semibold text-slate-800 text-sm">fisiotherapy.app</span>
                </div>
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-200" />
                  <div className="w-3 h-3 rounded-full bg-slate-200" />
                  <div className="w-3 h-3 rounded-full bg-slate-200" />
                </div>
              </div>
              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { label: "Pacientes activos", value: "128", icon: Users, color: "emerald" },
                  { label: "Citas hoy", value: "12", icon: Clock, color: "amber" },
                  { label: "Adherencia", value: "94%", icon: TrendingUp, color: "emerald" },
                ].map((stat, i) => (
                  <div key={i} className={`bg-${stat.color}-50 rounded-xl p-3`}>
                    <stat.icon className={`w-4 h-4 text-${stat.color}-500 mb-1`} />
                    <p className="text-lg font-bold text-slate-800 leading-tight">{stat.value}</p>
                    <p className="text-[10px] text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </div>
              {/* Appointments list */}
              <div className="space-y-2">
                {[
                  { time: "09:00", name: "Laura Reyes", type: "Lumbalgia — Sesion 3/8", status: "bg-emerald-500" },
                  { time: "10:30", name: "Miguel Castillo", type: "Post-operatorio rodilla", status: "bg-amber-400" },
                  { time: "12:00", name: "Ana Sofia Perez", type: "Cervicalgia — Evaluacion", status: "bg-slate-300" },
                ].map((apt, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2.5">
                    <span className="text-xs font-medium text-slate-400 w-10">{apt.time}</span>
                    <div className={`w-1.5 h-8 rounded-full ${apt.status}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{apt.name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{apt.type}</p>
                    </div>
                    <UserCheck className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>

            {/* Floating card - Appointment */}
            <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-4 shadow-lg animate-float hidden md:flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Proxima cita</p>
                <p className="text-sm font-semibold text-slate-800">Mar 10:00 - Rehabilitacion</p>
              </div>
            </div>

            {/* Floating card - Episode */}
            <div className="absolute -top-4 -right-4 glass-card rounded-2xl p-4 shadow-lg badge-float hidden md:flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Activity className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Episodio activo</p>
                <p className="text-sm font-semibold text-slate-800">Lumbalgia - Sesion 4/8</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
